<script setup>
// Interactive location presentation — Leaflet + OpenStreetMap tiles (no API
// key/account needed, unlike Google Maps JS or Mapbox). Renders only when
// the project has real latitude/longitude (Nexceo::Project#latitude/
// #longitude, admin-entered via Settings > NEXCEO Projects > Edit); falls
// back to the previous plain iframe-embed/link-out behavior otherwise — no
// invented pin, no invented coordinates.
//
// Surrounding highlights are the same admin-curated
// Nexceo::Project#knowledge_base['nearby_places'] the Knowledge page already
// manages (now extended with optional latitude/longitude/thumbnail_url —
// see ProjectKnowledge.vue), not a new data source. The 1/5/10km rings and
// each place's straight-line distance are computed client-side from
// coordinates alone (haversine — no API call). "Road Connectivity" shows
// real driving routes only where an admin has run "Recalculate Routes" on
// the Knowledge page (Nexceo::OsrmRouteService, free OSRM public server,
// server-side and cached — never called from this customer-facing tab).
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import projectKnowledgeAPI from 'dashboard/api/nexceo/projectKnowledge';

const props = defineProps({
  project: { type: Object, required: true },
  projectId: { type: Number, required: true },
});

const addressLines = computed(() =>
  [
    props.project.location,
    props.project.city,
    props.project.state,
    props.project.country,
  ].filter(Boolean)
);

const hasCoordinates = computed(
  () => props.project.latitude != null && props.project.longitude != null
);
const projectLat = computed(() => Number(props.project.latitude));
const projectLng = computed(() => Number(props.project.longitude));

const openMapsUrl = computed(() => {
  if (props.project.location_url) return props.project.location_url;
  if (hasCoordinates.value) {
    return `https://www.google.com/maps?q=${projectLat.value},${projectLng.value}`;
  }
  return null;
});

// Legacy fallback (no coordinates yet) — same behavior this tab always had.
const embedUrl = computed(() => {
  const url = props.project.location_url;
  if (!url) return null;
  return url.includes('output=embed') ? url : null;
});

const CATEGORY_META = {
  school: {
    label: 'School',
    icon: 'i-lucide-graduation-cap',
    markerClass: 'bg-violet-500',
    hex: '#8b5cf6',
  },
  hospital: {
    label: 'Hospital',
    icon: 'i-lucide-cross',
    markerClass: 'bg-red-500',
    hex: '#ef4444',
  },
  office: {
    label: 'Office',
    icon: 'i-lucide-briefcase',
    markerClass: 'bg-woot-500',
    hex: '#1f93ff',
  },
  it_park: {
    label: 'IT Park',
    icon: 'i-lucide-building-2',
    markerClass: 'bg-green-500',
    hex: '#22c55e',
  },
  shopping: {
    label: 'Shopping',
    icon: 'i-lucide-shopping-bag',
    markerClass: 'bg-yellow-500',
    hex: '#eab308',
  },
  connectivity: {
    label: 'Connectivity',
    icon: 'i-lucide-train-front',
    markerClass: 'bg-slate-500',
    hex: '#64748b',
  },
};
const DEFAULT_CATEGORY_META = {
  label: 'Place',
  icon: 'i-lucide-map-pin',
  markerClass: 'bg-slate-500',
  hex: '#64748b',
};
function categoryMeta(category) {
  return CATEGORY_META[category] || DEFAULT_CATEGORY_META;
}

function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const toRad = deg => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Real geographic point on a ring's circumference (bearing 180° = due
// south of center) — used to place each ring's label so it sits exactly
// on the ring at any zoom/pan, instead of a fixed pixel offset that only
// looks right at one zoom level.
function destinationPoint(lat, lng, bearingDeg, distanceKm) {
  const R = 6371;
  const bearing = (bearingDeg * Math.PI) / 180;
  const lat1 = (lat * Math.PI) / 180;
  const lng1 = (lng * Math.PI) / 180;
  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(distanceKm / R) +
      Math.cos(lat1) * Math.sin(distanceKm / R) * Math.cos(bearing)
  );
  const lng2 =
    lng1 +
    Math.atan2(
      Math.sin(bearing) * Math.sin(distanceKm / R) * Math.cos(lat1),
      Math.cos(distanceKm / R) - Math.sin(lat1) * Math.sin(lat2)
    );
  return [(lat2 * 180) / Math.PI, (lng2 * 180) / Math.PI];
}

const nearbyPlaces = ref([]);
const isLoadingPlaces = ref(true);

function hasPlaceCoords(place) {
  return (
    place.latitude !== null &&
    place.latitude !== undefined &&
    place.latitude !== '' &&
    place.longitude !== null &&
    place.longitude !== undefined &&
    place.longitude !== ''
  );
}

// Prefer a real computed road distance (Recalculate Routes), then whatever
// the admin typed by hand, then an auto-calculated straight-line distance —
// never fabricated, always from a real source.
function displayDistanceKm(place) {
  if (place.route_distance_km) return Number(place.route_distance_km);
  if (place.distance_km) return Number(place.distance_km);
  if (hasCoordinates.value && hasPlaceCoords(place)) {
    return (
      Math.round(
        haversineKm(
          projectLat.value,
          projectLng.value,
          Number(place.latitude),
          Number(place.longitude)
        ) * 10
      ) / 10
    );
  }
  return null;
}

const highlightPlaces = computed(() =>
  nearbyPlaces.value
    .map(place => ({ ...place, computedDistanceKm: displayDistanceKm(place) }))
    .sort(
      (a, b) =>
        (a.computedDistanceKm ?? Infinity) - (b.computedDistanceKm ?? Infinity)
    )
);

const routedPlaces = computed(() =>
  highlightPlaces.value.filter(
    p => p.route_distance_km && p.route_geometry?.length
  )
);

async function loadKnowledge() {
  isLoadingPlaces.value = true;
  try {
    const { data } = await projectKnowledgeAPI.show(props.projectId);
    nearbyPlaces.value = Array.isArray(data.nearby_places)
      ? data.nearby_places
      : [];
  } catch {
    nearbyPlaces.value = [];
  } finally {
    isLoadingPlaces.value = false;
  }
}

// ── Leaflet map ──────────────────────────────────────────────────────────
const mapEl = ref(null);
const RING_KM = [1, 5, 10];
const selectedRouteIndex = ref(null);
let map = null;
let ringLayer = null;
let placeLayer = null;
let routeLayer = null;

function escapeHtml(str) {
  return String(str || '').replace(
    /[&<>"']/g,
    c =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      })[c]
  );
}

function projectDivIcon() {
  const photo = props.project.hero_image_url;
  const inner = photo
    ? `<div class="size-11 rounded-full ring-4 ring-woot-500 shadow-lg bg-cover bg-center" style="background-image:url('${photo}')"></div>`
    : `<div class="flex items-center justify-center size-11 rounded-full ring-4 ring-woot-500 shadow-lg bg-woot-500 text-white"><span class="i-lucide-building-2 size-5"></span></div>`;
  return L.divIcon({
    className: '',
    html: `<div class="flex flex-col items-center gap-1">${inner}<span class="px-1.5 py-0.5 rounded bg-woot-600 text-white text-[10px] font-semibold whitespace-nowrap shadow">${escapeHtml(props.project.name)}</span></div>`,
    iconSize: [44, 60],
    iconAnchor: [22, 44],
  });
}

function placeDivIcon(place) {
  const meta = categoryMeta(place.category);
  return L.divIcon({
    className: '',
    html: `<div class="flex flex-col items-center gap-0.5"><div class="flex items-center justify-center size-7 rounded-full ${meta.markerClass} text-white ring-2 ring-white shadow-md"><span class="${meta.icon} size-3.5"></span></div><span class="px-1 py-0.5 rounded bg-n-background/95 text-n-slate-12 text-[10px] font-medium whitespace-nowrap shadow">${escapeHtml(place.name || meta.label)}</span></div>`,
    iconSize: [90, 44],
    iconAnchor: [14, 14],
  });
}

function drawRings() {
  ringLayer = L.layerGroup().addTo(map);
  RING_KM.forEach(km => {
    L.circle([projectLat.value, projectLng.value], {
      radius: km * 1000,
      color: '#1f93ff',
      weight: 1.5,
      dashArray: '6,6',
      fillOpacity: 0.03,
      className: 'nexceo-map-ring',
    }).addTo(ringLayer);
    // Real point on the ring's own circumference (due south of center) so
    // the label always sits exactly on that ring at any zoom/pan level.
    const [labelLat, labelLng] = destinationPoint(
      projectLat.value,
      projectLng.value,
      180,
      km
    );
    L.marker([labelLat, labelLng], {
      icon: L.divIcon({
        className: '',
        html: `<span class="px-1.5 py-0.5 rounded-full bg-woot-500 text-white text-[10px] font-bold shadow whitespace-nowrap">${km} KM</span>`,
        iconSize: [40, 18],
        iconAnchor: [20, 9],
      }),
      interactive: false,
    }).addTo(ringLayer);
  });
}

function drawPlaces() {
  placeLayer = L.layerGroup().addTo(map);
  highlightPlaces.value.forEach(place => {
    if (!hasPlaceCoords(place)) return;
    const marker = L.marker([Number(place.latitude), Number(place.longitude)], {
      icon: placeDivIcon(place),
    }).addTo(placeLayer);
    const dist =
      place.computedDistanceKm != null ? `${place.computedDistanceKm} km` : '';
    marker.bindPopup(`<strong>${escapeHtml(place.name)}</strong><br />${dist}`);
  });
}

function drawRoutes() {
  routeLayer = L.layerGroup().addTo(map);
  routedPlaces.value.forEach((place, i) => {
    const latlngs = place.route_geometry.map(([lng, lat]) => [lat, lng]);
    const meta = categoryMeta(place.category);
    L.polyline(latlngs, {
      color: meta.hex,
      weight: selectedRouteIndex.value === i ? 4 : 2,
      opacity:
        selectedRouteIndex.value === null || selectedRouteIndex.value === i
          ? 0.8
          : 0.25,
    }).addTo(routeLayer);
  });
}

function redrawRoutes() {
  routeLayer?.remove();
  drawRoutes();
}

function focusRoute(index) {
  selectedRouteIndex.value = selectedRouteIndex.value === index ? null : index;
  redrawRoutes();
  const place = routedPlaces.value[index];
  if (place && selectedRouteIndex.value !== null) {
    const latlngs = place.route_geometry.map(([lng, lat]) => [lat, lng]);
    map.fitBounds(L.latLngBounds(latlngs), { padding: [40, 40] });
  }
}

function fitToContent() {
  const points = [[projectLat.value, projectLng.value]];
  highlightPlaces.value.forEach(p => {
    if (hasPlaceCoords(p))
      points.push([Number(p.latitude), Number(p.longitude)]);
  });
  if (points.length > 1) {
    map.fitBounds(L.latLngBounds(points), { padding: [50, 50] });
  }
}

async function initMap() {
  if (!hasCoordinates.value) return;
  await nextTick();
  if (!mapEl.value) return;
  map = L.map(mapEl.value, {
    scrollWheelZoom: true,
    attributionControl: false,
  }).setView([projectLat.value, projectLng.value], 13);
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    maxZoom: 19,
  }).addTo(map);
  // OpenStreetMap's tile usage policy requires visible attribution when
  // using their free tiles — shown here as small as reasonably legible
  // (see .nexceo-map-attribution below) rather than removed, and without
  // Leaflet's own optional self-promotional prefix.
  L.control
    .attribution({ prefix: false, position: 'bottomright' })
    .addAttribution(
      '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a>'
    )
    .addTo(map);

  L.marker([projectLat.value, projectLng.value], {
    icon: projectDivIcon(),
    zIndexOffset: 1000,
  }).addTo(map);
  drawRings();
  drawPlaces();
  drawRoutes();
  fitToContent();
}

// ── Fullscreen (for sales presentations) ────────────────────────────────
const fullscreenEl = ref(null);
const isFullscreen = ref(false);
function onFullscreenChange() {
  isFullscreen.value = document.fullscreenElement === fullscreenEl.value;
  // Leaflet caches the map container's pixel size — without this it stays
  // sized for the pre-fullscreen layout until the next manual resize.
  nextTick(() => map?.invalidateSize());
}
function toggleFullscreen() {
  if (document.fullscreenElement) {
    document.exitFullscreen();
  } else {
    fullscreenEl.value?.requestFullscreen();
  }
}

onMounted(async () => {
  await loadKnowledge();
  await initMap();
  document.addEventListener('fullscreenchange', onFullscreenChange);
});

onBeforeUnmount(() => {
  document.removeEventListener('fullscreenchange', onFullscreenChange);
  map?.remove();
  map = null;
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="flex-1 overflow-y-auto">
    <div class="flex flex-col gap-4 p-6 h-full">
      <!-- Header -->
      <div class="flex items-start justify-between gap-4 flex-wrap shrink-0">
        <div class="flex items-start gap-3">
          <span
            class="flex items-center justify-center size-10 rounded-xl bg-woot-50 dark:bg-woot-500/10 shrink-0"
          >
            <span
              class="i-lucide-map-pin size-5 text-woot-600 dark:text-woot-400"
            />
          </span>
          <div>
            <h1 class="text-base font-semibold text-n-slate-12">
              Project Location
            </h1>
            <p v-if="addressLines.length" class="text-sm text-n-slate-9 mt-0.5">
              {{ addressLines.join(', ') }}
            </p>
            <p v-else class="text-sm text-n-slate-8 mt-0.5">
              No location details added yet.
            </p>
          </div>
        </div>
        <div class="flex items-center gap-2 shrink-0">
          <a
            v-if="openMapsUrl"
            :href="openMapsUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg border border-n-weak hover:bg-n-alpha-2 transition-colors"
          >
            Open in Google Maps
            <span class="i-lucide-external-link size-3.5" />
          </a>
          <button
            v-if="hasCoordinates"
            type="button"
            class="flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg border border-n-weak hover:bg-n-alpha-2 transition-colors"
            title="Full screen — ideal for presenting to customers"
            @click="toggleFullscreen"
          >
            <span
              :class="isFullscreen ? 'i-lucide-minimize' : 'i-lucide-maximize'"
              class="size-3.5"
            />
            {{ isFullscreen ? 'Exit Full Screen' : 'Full Screen' }}
          </button>
        </div>
      </div>

      <!-- Interactive map + sidebars — flex-1 so this row fills whatever
           vertical space is left under the header instead of sitting at a
           fixed height with blank space below it (the outer wrapper above
           is the actual scroll container, so a tall sidebar still scrolls
           safely if it ever exceeds the available height). -->
      <div
        v-if="hasCoordinates"
        ref="fullscreenEl"
        class="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4 items-stretch min-h-[420px]"
        :class="isFullscreen ? 'bg-n-background p-4 h-screen' : 'flex-1 min-h-0'"
      >
        <div
          class="rounded-xl overflow-hidden border border-n-weak w-full z-0 min-h-0 h-full"
        >
          <!-- Leaflet takes ownership of this element's classList/style on
               init (adds "leaflet-container" etc. outside Vue's knowledge) —
               it must never carry a reactive :class binding of its own, or
               Vue's next patch of this element overwrites the whole class
               attribute and silently strips what Leaflet added, breaking
               the map's positioning CSS. -->
          <div ref="mapEl" class="w-full h-full" />
        </div>

        <div class="flex flex-col gap-4 h-full overflow-y-auto">
          <!-- Surrounding Highlights -->
          <div class="rounded-xl border border-n-weak p-4 flex flex-col gap-3">
            <h2 class="text-sm font-semibold text-n-slate-12">
              Surrounding Highlights
            </h2>
            <p
              v-if="!isLoadingPlaces && !highlightPlaces.length"
              class="text-xs text-n-slate-7"
            >
              No surrounding places added yet. Add them from Settings → NEXCEO
              Projects → Knowledge.
            </p>
            <div v-else class="flex flex-col gap-2 max-h-64 overflow-y-auto">
              <div
                v-for="place in highlightPlaces"
                :key="place.name + place.category"
                class="flex items-center gap-2"
              >
                <span
                  class="flex items-center justify-center size-6 rounded-full text-white shrink-0"
                  :class="categoryMeta(place.category).markerClass"
                >
                  <span
                    :class="categoryMeta(place.category).icon"
                    class="size-3"
                  />
                </span>
                <span class="text-xs text-n-slate-11 truncate flex-1">
                  {{ place.name }}
                </span>
                <span
                  v-if="place.computedDistanceKm != null"
                  class="text-xs font-semibold text-n-slate-12 shrink-0"
                >
                  {{ place.computedDistanceKm }} km
                </span>
              </div>
            </div>
          </div>

          <!-- Road Connectivity -->
          <div
            v-if="routedPlaces.length"
            class="rounded-xl border border-n-weak p-4 flex flex-col gap-3"
          >
            <h2 class="text-sm font-semibold text-n-slate-12">
              Road Connectivity
            </h2>
            <p class="text-[11px] text-n-slate-7">
              Real driving routes to {{ project.name }}. Tap one to trace it on
              the map.
            </p>
            <div class="flex flex-col gap-2">
              <button
                v-for="(place, i) in routedPlaces"
                :key="place.name + place.category"
                type="button"
                class="flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg border transition-colors text-left"
                :class="
                  selectedRouteIndex === i
                    ? 'border-woot-400 bg-woot-50 dark:bg-woot-500/10'
                    : 'border-n-weak hover:bg-n-alpha-2'
                "
                @click="focusRoute(i)"
              >
                <span class="flex items-center gap-2 min-w-0">
                  <span
                    class="size-2 rounded-full shrink-0"
                    :style="{
                      backgroundColor: categoryMeta(place.category).hex,
                    }"
                  />
                  <span class="text-xs font-medium text-n-slate-12 truncate">
                    {{ place.name }}
                  </span>
                </span>
                <span
                  class="text-[11px] font-semibold text-n-slate-12 shrink-0 whitespace-nowrap"
                >
                  {{ place.route_distance_km }} km ·
                  {{ place.route_duration_minutes }} min
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Fallback: no coordinates set yet — unchanged legacy behavior -->
      <template v-else>
        <div
          v-if="embedUrl"
          class="rounded-xl overflow-hidden border border-n-weak h-[460px]"
        >
          <iframe
            :src="embedUrl"
            class="w-full h-full border-0"
            loading="lazy"
            referrerpolicy="no-referrer-when-downgrade"
            title="Project location map"
          />
        </div>

        <a
          v-else-if="project.location_url"
          :href="project.location_url"
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center justify-center gap-2 py-16 rounded-xl border border-dashed border-n-weak text-sm font-medium text-woot-600 dark:text-woot-400 hover:bg-n-alpha-1 transition-colors"
        >
          <span class="i-lucide-map size-5" />
          Open in Google Maps
          <span class="i-lucide-external-link size-3.5" />
        </a>

        <div
          v-else
          class="flex flex-col items-center justify-center gap-2 py-16 rounded-xl border border-dashed border-n-weak text-center"
        >
          <span class="i-lucide-map-pinned size-7 text-n-slate-6" />
          <p class="text-sm font-medium text-n-slate-9">
            No map link added for this project yet.
          </p>
          <p class="text-xs text-n-slate-7 max-w-xs">
            An admin can add coordinates from Settings → NEXCEO Projects → Edit
            for an interactive map with surrounding highlights.
          </p>
        </div>
      </template>
    </div>
  </div>
</template>

<style>
/* Unscoped intentionally — Leaflet renders these elements imperatively,
   outside Vue's render tree, so scoped attribute selectors never match
   them. Only targets this tab's own map instance via .nexceo-map-ring. */
@keyframes nexceo-ring-pulse {
  0%,
  100% {
    stroke-opacity: 0.85;
    fill-opacity: 0.03;
  }
  50% {
    stroke-opacity: 0.25;
    fill-opacity: 0.12;
  }
}
.nexceo-map-ring {
  animation: nexceo-ring-pulse 3s ease-in-out infinite;
}

/* Required OpenStreetMap attribution, shrunk to the smallest reasonably
   legible size rather than removed — see the tile usage policy note in
   initMap(). */
.leaflet-control-attribution {
  font-size: 9px;
  padding: 0 4px;
  opacity: 0.6;
  background: rgba(255, 255, 255, 0.6);
}
</style>
